import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TrashIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { Product } from "../common/models/product";
import {
  extractMaterialString,
  formatDimensions,
} from "./InventorySearch/helpers";
import { useReactToPrint } from "react-to-print";
import { useDimensionsUnit } from "../buyerStateDefs";

type BookmarkedItemsProps = {
  embedded?: boolean;
};

export const BookmarkedItems = (props: BookmarkedItemsProps) => {
  const [savedProducts, setSavedProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const pdfComponentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef: pdfComponentRef });
  const dimensionUnit = useDimensionsUnit((state) => state.dimensionsUnit);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const q = searchParams.get("q") || "";
    console.log("Search", q);
    setSearchQuery(q);
  }, [location.search]);

  useEffect(() => {
    const saved = localStorage.getItem("savedProducts");
    if (saved) {
      setSavedProducts(JSON.parse(saved));
    }
  }, []);

  const handleRemoveSavedItemFn = (product: Product) => {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const updatedProducts = savedProducts.filter(
        (item) => item.id !== product.id,
      );
      setSavedProducts(updatedProducts);
      localStorage.setItem("savedProducts", JSON.stringify(updatedProducts));
    };
  };

  const displaySavedItemRow = (product: Product) => {
    return (
      <li
        key={product.id}
        className="py-4 flex items-center justify-between space-x-4 px-4 sm:px-6 hover:bg-slate-50 cursor-pointer"
        onClick={() => {
          navigate(`/product?id=${product.id}&q=${searchQuery || ""}`, {
            state: { previousUrl: window.location.href },
          });
        }}
      >
        <div className="flex min-w-0 flex-1 items-center space-x-3">
          <span className="text-sm font-medium text-gray-900">
            {extractMaterialString(product)} {product.shape}
          </span>
          <span className="text-sm text-gray-500">
            {formatDimensions(product, dimensionUnit)}
          </span>
          <span className="text-sm text-gray-500">{product.location}</span>
        </div>
        <div className="flex justify-between space-x-2">
          <div>
            <button
              type="button"
              className="cursor-pointer"
              onClick={handleRemoveSavedItemFn(product)}
            >
              <TrashIcon className="w-3 h-3 text-gray-600 mt-1 hover:text-red-500" />
            </button>
          </div>
        </div>
      </li>
    );
  };

  if (savedProducts.length > 0) {
    return (
      <>
        <div className="hidden print:block">
          <div ref={pdfComponentRef}>
            <style>{`
            @media print {
              .pdf-container {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                color: #111827;
                line-height: 1.6;
                max-width: 100%;
                margin: 0;
                padding: 20px;
              }
              .pdf-header {
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 20px;
                color: #111827;
                border-bottom: 2px solid #e5e7eb;
                padding-bottom: 10px;
              }
              .pdf-table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 16px;
                border: 1px solid #d1d5db;
              }
              .pdf-table th {
                background-color: #f9fafb;
                padding: 12px;
                text-align: left;
                font-weight: 600;
                font-size: 14px;
                color: #374151;
                border-bottom: 1px solid #d1d5db;
                border-right: 1px solid #e5e7eb;
              }
              .pdf-table th:last-child {
                border-right: none;
              }
              .pdf-table td {
                padding: 12px;
                font-size: 14px;
                color: #111827;
                border-bottom: 1px solid #e5e7eb;
                border-right: 1px solid #e5e7eb;
                vertical-align: top;
              }
              .pdf-table td:last-child {
                border-right: none;
              }
              .pdf-table tr:last-child td {
                border-bottom: none;
              }
              .pdf-table tr:nth-child(even) {
                background-color: #f9fafb;
              }
              .pdf-product-name {
                font-weight: 500;
                color: #111827;
                margin-bottom: 2px;
              }
              .pdf-dimensions {
                color: #6b7280;
                font-size: 13px;
              }
              .pdf-location {
                color: #6b7280;
                font-size: 13px;
              }
              .pdf-footer {
                margin-top: 30px;
                padding-top: 15px;
                border-top: 1px solid #e5e7eb;
                font-size: 12px;
                color: #6b7280;
                text-align: center;
              }
            }
          `}</style>
            <div className="pdf-container">
              <h2 className="pdf-header">Selected Products from Kono</h2>
              <table className="pdf-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Dimensions</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {savedProducts.map((product) => (
                    <tr key={product.id}>
                      <td>
                        <div className="pdf-product-name">
                          {extractMaterialString(product)} {product.shape}
                        </div>
                      </td>
                      <td>
                        <div className="pdf-dimensions">
                          {formatDimensions(product, dimensionUnit)}
                        </div>
                      </td>
                      <td>
                        <div className="pdf-location">{product.location}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pdf-footer">
                Generated from Kono - {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-5 sm:px-6">
          {process.env.REACT_APP_SHOW_CART_BACK_BUTTON === "true" &&
            !props.embedded && (
              <div className="pb-4">
                {/* A Back link to navigate backwards using react router dom, equivalent of the browser back button */}
                <button
                  onClick={() => navigate(-1)}
                  className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  &larr; Back
                </button>
              </div>
            )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Bookmarked ({savedProducts.length})
            </h3>
            <button
              className="flex items-center self-start text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
              onClick={reactToPrintFn}
            >
              <DocumentArrowDownIcon className="h-3 w-3 mr-1" />
              Download as PDF
            </button>
          </div>
        </div>
        <div>
          <ul className="divide-y divide-gray-200 border-b border-gray-200">
            {savedProducts.map((product) => displaySavedItemRow(product))}
          </ul>
        </div>
      </>
    );
  } else if (!props.embedded) {
    return (
      <div className="border-t border-gray-200 mt-8">
        <div className="px-4 py-5 sm:px-6 text-gray-500">
          No bookmarked items yet.
        </div>
      </div>
    );
  } else {
    return null;
  }
};
